import { ChevronLeftIcon, WarningIconDeprecated } from '@village/icons';
import { Text } from '@village/ui';
import { Banner, Props as BannerProps } from 'components/banner';
import { SelectedAppointmentDetailsCard } from 'components/selected-appointment-details-card';
import { PatientInformationForm } from 'forms/patient-information/form';
import { PatientInformationFormInputs } from 'forms/patient-information/form-inputs';
import { PatientInformationFormSchemaValidated } from 'forms/patient-information/schema';
import {
    useBooking,
    useCaptureCountlyEvent,
    useIsSelectedAppointmentLoading,
    useScrollToTop,
    useSyncBookingWithUrl,
} from 'hooks';
import { addGoogleTagManagerEvent } from 'modules/google-tag-manager';
import { path, toPairs } from 'ramda';
import { useCallback, useTransition } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import * as Styled from './styles';

const PatientInformationPage = () => {
    useScrollToTop();
    useSyncBookingWithUrl();

    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { addCountlyEvent } = useCaptureCountlyEvent();
    const { booking, setBookingField } = useBooking();
    const isAppointmentLoading = useIsSelectedAppointmentLoading(booking);
    const startTransition = useTransition()[1];

    const patientInformationMissing = path<boolean>(['patientInformationMissing'], location.state);
    const appointmentUnavailable = !isAppointmentLoading && !booking.appointment;

    const handleSubmit = useCallback(
        async (patientInformation: PatientInformationFormSchemaValidated) => {
            if (booking.appointment) {
                addCountlyEvent({ key: 'step4.completeForm' });
                addGoogleTagManagerEvent('review-details', booking);

                startTransition(() => {
                    setBookingField({ patientInformation });
                });
                navigate({ pathname: '/review-details', search: location.search });
            }
        },
        [setBookingField, booking, navigate, addCountlyEvent, location.search]
    );

    const onError: SubmitErrorHandler<PatientInformationFormSchemaValidated> = useCallback(
        (errors) => {
            toPairs(errors).forEach(([fieldName, error]) => {
                addCountlyEvent({
                    key: 'step4.formErrors',
                    segmentation: {
                        field: fieldName,
                        message: error?.message,
                    },
                });
            });
        },
        [addCountlyEvent]
    );

    const goToAppointmentsPage = useCallback(() => {
        // Previous page will have been set if we came from a widget
        // And will allow us to go back to the widget
        const previousPage = searchParams.get('previousPage');
        if (previousPage) {
            window.location.assign(decodeURIComponent(previousPage));
        } else {
            navigate({ pathname: '/select-appointment', search: location.search }, { replace: true });
        }
    }, [navigate, location.search, searchParams]);

    return (
        <Styled.Container>
            <Styled.FormContainer>
                <Styled.BackButton onClick={goToAppointmentsPage}>
                    <ChevronLeftIcon size="md" />
                </Styled.BackButton>
                <div>
                    <Styled.Title type="h3">Enter details</Styled.Title>

                    {patientInformationMissing || appointmentUnavailable ? (
                        <Styled.WarningContainer data-testid="patient-information-missing-warning">
                            <Banner
                                status="info"
                                data-testid="patient-information-missing"
                                icon={WarningIconDeprecated as BannerProps['icon']}
                            >
                                <Text type="body1">
                                    {patientInformationMissing
                                        ? 'We’re sorry, your information was not saved. Please re-enter and try booking again.'
                                        : 'Please select a new appointment slot.'}
                                </Text>
                            </Banner>
                        </Styled.WarningContainer>
                    ) : null}

                    <PatientInformationForm onSubmit={handleSubmit} onError={onError}>
                        <PatientInformationFormInputs disabled={appointmentUnavailable} />
                    </PatientInformationForm>
                </div>
            </Styled.FormContainer>
            <SelectedAppointmentDetailsCard />
        </Styled.Container>
    );
};

export { PatientInformationPage };
